import { z } from 'zod'

import {
  PracticeTestOptionSchema,
  ProblemLevelSchema,
  ProblemSourceTypeSchema,
  ProblemTypeSchema,
  SimilarLevelSchema,
} from '~/@entities/Problem'
import { StudentIdSchema } from '~/@entities/Student'
import {
  CustomWorkbookSchema,
  WorkbookSecuringTypeSchema,
  WorkbookTypeSchema,
} from '~/@entities/Workbook'

/***************** 교재 만들기 방식 *****************/
// 무엇을 바탕으로(유형·원본 교재·학교별 기출) 어떻게 묶는가(단원·단계)
// - CONCEPT_CHAPTER: 단원·유형 기반, 단계 없이 단원으로 묶음
// - CONCEPT_STEP: 단원·유형 기반, 학습 단계로 묶음
// - WORKBOOK_PROBLEM_CHAPTER: 원본 교재 문제 기반, 단원으로 묶음
// - WORKBOOK_PROBLEM_STEP: 원본 교재 문제 기반, 학습 단계로 묶음
// - SCHOOL_TEST_PROBLEM_STEP: 학교별 기출 기반, 학습 단계로 묶음
export const CUSTOM_WORKBOOK_TYPE = [
  'CONCEPT_CHAPTER',
  'CONCEPT_STEP',
  'WORKBOOK_PROBLEM_CHAPTER',
  'WORKBOOK_PROBLEM_STEP',
  'SCHOOL_TEST_PROBLEM_STEP',
] as const

/***************** 학습 단계 *****************/
// 개념 이해·유형 연습·응용 도전·단원 마무리
export const CUSTOM_WORKBOOK_STEP = ['THEORY', 'EXERCISE', 'CHALLENGE', 'WRAP_UP'] as const
// 학교별 기출 교재의 단계. 개념 추가·기출 유형·교과서 문제·실전 모의고사
export const SCHOOL_TEST_CUSTOM_WORKBOOK_STEP = [
  'THEORY',
  'PAIR_SIMILAR',
  'SCHOOL_WORKBOOK',
  'PRACTICE_TEST',
] as const

/***************** 묶는 단원 단위 *****************/
export const GROUPING_CHAPTER_TYPE = ['MIDDLE_CHAPTER', 'LITTLE_CHAPTER'] as const

/***************** 기존 출제 문제 제외 단위 *****************/
// 학원이 만든 교재·로그인한 선생님이 만든 교재·대상 학생에게 출제한 교재의 문제를 뺀다
export const PREVIOUS_EXCLUSION_SCOPE = [
  'ACADEMY_CREATED',
  'TEACHER_CREATED',
  'STUDENT_ASSIGNED',
] as const

export const CustomWorkbookTypeSchema = z.enum(CUSTOM_WORKBOOK_TYPE)
export type CustomWorkbookType = z.infer<typeof CustomWorkbookTypeSchema>
export type ChapterCustomWorkbookType = Extract<
  CustomWorkbookType,
  'CONCEPT_CHAPTER' | 'WORKBOOK_PROBLEM_CHAPTER'
>
export type StepCustomWorkbookType = Extract<
  CustomWorkbookType,
  'CONCEPT_STEP' | 'WORKBOOK_PROBLEM_STEP'
>

export const CustomWorkbookStepSchema = z.enum(CUSTOM_WORKBOOK_STEP)
export type CustomWorkbookStep = z.infer<typeof CustomWorkbookStepSchema>

export const SchoolTestCustomWorkbookStepSchema = z.enum(SCHOOL_TEST_CUSTOM_WORKBOOK_STEP)
export type SchoolTestCustomWorkbookStep = z.infer<typeof SchoolTestCustomWorkbookStepSchema>

export const GroupingChapterTypeSchema = z.enum(GROUPING_CHAPTER_TYPE)
export type GroupingChapterType = z.infer<typeof GroupingChapterTypeSchema>

export const PreviousExclusionScopeSchema = z.enum(PREVIOUS_EXCLUSION_SCOPE)
export type PreviousExclusionScope = z.infer<typeof PreviousExclusionScopeSchema>

/***************** 문제 조건 *****************/

const _CommonFilterSchema = z.object({
  // 이전에 교재로 쓴 문제를 뺄 것인가
  excludePrevious: z.boolean(),
  previousExclusionScope: PreviousExclusionScopeSchema.nullable(),
  // previousExclusionScope 가 STUDENT_ASSIGNED 일 때 출제 이력을 볼 학생
  studentIds: z.array(StudentIdSchema).nullable(),
  // 교육과정 밖 유형의 문제를 뺄 것인가
  excludeOutOfCurriculum: z.boolean(),
  autoScorableOnly: z.boolean(),
})

const _AnswerFilterSchema = z.object({
  answerTypes: z.array(ProblemTypeSchema),
  practiceTestOption: PracticeTestOptionSchema,
})

// 원본 교재를 바탕으로 쌍둥이·유사 문제를 붙인다
const _WorkbookProblemFilterSchema = z.object({
  // 쌍둥이·유사 문제 배수(0~3)
  pairX: z.number(),
  similarX: z.number(),
  similarLevel: SimilarLevelSchema,
  // 바탕이 되는 시중교재·교과서·커스텀 시그니처 교재와 그 문제
  originSupportedWorkbookId: z.number(),
  originSupportedWorkbookProblemIds: z.array(z.number()),
  exampleProblemIds: z.array(z.number()),
})

const _ConceptFilterSchema = z.object({
  conceptIds: z.array(z.number()),
  level: ProblemLevelSchema,
  maxProblemCount: z.number(),
  excludedTopicIds: z.array(z.number()),
  excludedSubTopicIds: z.array(z.number()),
})

export const ConceptChapterFilterSchema = _CommonFilterSchema
  .extend(_AnswerFilterSchema.shape)
  .extend(_ConceptFilterSchema.shape)
  .extend({
    customWorkbookType: z.literal('CONCEPT_CHAPTER'),
    groupingChapterType: GroupingChapterTypeSchema,
  })

export const ConceptStepFilterSchema = _CommonFilterSchema
  .extend(_AnswerFilterSchema.shape)
  .extend(_ConceptFilterSchema.shape)
  .extend({
    customWorkbookType: z.literal('CONCEPT_STEP'),
    steps: z.array(CustomWorkbookStepSchema),
  })

export const WorkbookProblemChapterFilterSchema = _CommonFilterSchema
  .extend(_AnswerFilterSchema.shape)
  .extend(_WorkbookProblemFilterSchema.shape)
  .extend({
    customWorkbookType: z.literal('WORKBOOK_PROBLEM_CHAPTER'),
    groupingChapterType: GroupingChapterTypeSchema,
    // 유형당 문제 수 상한(1~10). null 이면 제한 없음
    limitProblemCountPerConcept: z.number().nullable(),
  })

export const WorkbookProblemStepFilterSchema = _CommonFilterSchema
  .extend(_AnswerFilterSchema.shape)
  .extend(_WorkbookProblemFilterSchema.shape)
  .extend({
    customWorkbookType: z.literal('WORKBOOK_PROBLEM_STEP'),
    level: ProblemLevelSchema,
    steps: z.array(CustomWorkbookStepSchema),
  })

// 제외한 서버 필드: level(응답에는 실리지만 학교별 기출 요청이 받지 않는 값이라 조건이 아니다)
export const SchoolTestProblemStepFilterSchema = _CommonFilterSchema.extend({
  customWorkbookType: z.literal('SCHOOL_TEST_PROBLEM_STEP'),
  conceptIds: z.array(z.number()),
  maxProblemCount: z.number(),
  steps: z.array(SchoolTestCustomWorkbookStepSchema),
  // 기출 유형 쌍둥이 배수(1~5)
  pairX: z.number(),
  originSchoolTestIds: z.array(z.number()),
  originPaperIds: z.array(z.number()),
  // 실전 모의고사 회차 수. null 이면 넣지 않는다. 서버가 요청에서 1~5 로 검증한다
  practiceTestRound: z.literal([1, 2, 3, 4, 5]).nullable(),
  schoolWorkbookPublishers: z.array(z.string()).nullable(),
  schoolTitle: z.string(),
})

/** 교재 만들기의 문제 조건. 만드는 방식마다 받는 조건이 다르다 */
export const CustomWorkbookFilterSchema = z.discriminatedUnion('customWorkbookType', [
  ConceptChapterFilterSchema,
  ConceptStepFilterSchema,
  WorkbookProblemChapterFilterSchema,
  WorkbookProblemStepFilterSchema,
  SchoolTestProblemStepFilterSchema,
])
export type CustomWorkbookFilter<T extends CustomWorkbookType = CustomWorkbookType> = Extract<
  z.infer<typeof CustomWorkbookFilterSchema>,
  { customWorkbookType: T }
>

/***************** 문제 트리 *****************/
// 추천 결과와 저장된 내 교재의 문제는 단원·단계·유형·회차로 묶인 트리로 온다. 트리 모양은 만드는 방식이 정한다.
// - 단원 묶음: 중단원 → 문제 또는 소단원 → 문제
// - 학습 단계: 중단원 → [소단원(개념 이해) → 문제, 중단원(유형 연습) → 유형 → 문제, 중단원(응용 도전·단원 마무리) → 문제]
// - 학교별 기출: 중단원 → [소단원(개념 추가), 중단원(기출 유형·교과서 문제) → 유형 → 문제] 와 맨 끝의
//   중단원(실전 모의고사, id -1) → 회차 → 문제
// 서버는 이 트리를 저장 요청에 그대로 다시 받는다.

const _ChapterStepSchema = z.union([CustomWorkbookStepSchema, SchoolTestCustomWorkbookStepSchema])

export const CustomWorkbookTreeProblemSchema = z.object({
  id: z.number(),
  type: z.literal('PROBLEM'),
  answerType: ProblemTypeSchema,
  answerRate: z.number(),
  level: ProblemLevelSchema,
  trendy: z.boolean(),
  problemImageUrl: z.string(),
  answerImageUrl: z.string(),
  solutionImageUrl: z.string(),
  concept: z.object({ id: z.number(), name: z.string(), orderingNumber: z.number() }),
  favorite: z.boolean(),
  tagTop: z.string().nullable(),
  // 추천·조회 응답에만 있다. 저장 요청의 문제 노드는 이 값을 받지 않아 저장된 트리(PDF 생성)에는 없다
  sourceType: ProblemSourceTypeSchema.optional(),
  // 학교별 기출 시험지에서 온 문제의 원본 위치
  problemSource: z
    .object({ paperId: z.number(), paperBoxIndex: z.number(), imageUrl: z.string() })
    .nullable(),
})
export type CustomWorkbookTreeProblem = z.infer<typeof CustomWorkbookTreeProblemSchema>

/** 기준 문제의 쌍둥이·유사 문제. 교재 만들기 트리에 넣을 문제 칸 모양으로 온다 */
export const PairAndSimilarProblemsSchema = z.object({
  pairProblems: z.array(CustomWorkbookTreeProblemSchema),
  similarProblems: z.array(CustomWorkbookTreeProblemSchema),
})
export type PairAndSimilarProblems = z.infer<typeof PairAndSimilarProblemsSchema>

export const CustomWorkbookTreeConceptSchema = z.object({
  id: z.number(),
  type: z.literal('CONCEPT'),
  name: z.string(),
  url: z.string().nullable(),
  children: z.array(CustomWorkbookTreeProblemSchema),
})
export type CustomWorkbookTreeConcept = z.infer<typeof CustomWorkbookTreeConceptSchema>

export const CustomWorkbookTreeLittleChapterSchema = z.object({
  id: z.number(),
  type: z.literal('LITTLE_CHAPTER'),
  name: z.string(),
  url: z.string().nullable(),
  step: _ChapterStepSchema.nullable(),
  bigChapterName: z.string(),
  middleChapterName: z.string(),
  children: z.array(CustomWorkbookTreeProblemSchema),
})
export type CustomWorkbookTreeLittleChapter = z.infer<typeof CustomWorkbookTreeLittleChapterSchema>

export const CustomWorkbookTreePracticeTestRoundSchema = z.object({
  id: z.number(),
  type: z.literal('PRACTICE_TEST_ROUND'),
  name: z.string(),
  round: z.number(),
  step: z.literal('PRACTICE_TEST'),
  children: z.array(CustomWorkbookTreeProblemSchema),
})
export type CustomWorkbookTreePracticeTestRound = z.infer<
  typeof CustomWorkbookTreePracticeTestRoundSchema
>

const _middleChapter = <Child extends z.ZodType>(child: Child) =>
  z.object({
    id: z.number(),
    type: z.literal('MIDDLE_CHAPTER'),
    name: z.string(),
    step: _ChapterStepSchema.nullable(),
    bigChapterName: z.string(),
    littleChapters: z
      .array(z.object({ id: z.number(), name: z.string(), url: z.string().nullable() }))
      .nullable(),
    children: z.array(child),
  })

export type CustomWorkbookTreeMiddleChapter<Child> = Omit<
  z.infer<ReturnType<typeof _middleChapter>>,
  'children'
> & { children: Child[] }

// 단원 묶음
export const MiddleChapterTreeSchema = z.array(_middleChapter(CustomWorkbookTreeProblemSchema))
export const LittleChapterTreeSchema = z.array(CustomWorkbookTreeLittleChapterSchema)

// 학습 단계
const _stepChapter = <Step extends CustomWorkbookStep, Child extends z.ZodType>(
  step: Step,
  child: Child,
) => _middleChapter(child).extend({ step: z.literal(step) })

export const StepTreeSchema = z.array(
  _middleChapter(
    z.union([
      CustomWorkbookTreeLittleChapterSchema.extend({ step: z.literal('THEORY') }),
      _stepChapter('EXERCISE', CustomWorkbookTreeConceptSchema),
      _stepChapter('CHALLENGE', CustomWorkbookTreeProblemSchema),
      _stepChapter('WRAP_UP', CustomWorkbookTreeProblemSchema),
    ]),
  ),
)

// 학교별 기출
const _schoolTestStepChapter = <Step extends SchoolTestCustomWorkbookStep, Child extends z.ZodType>(
  step: Step,
  child: Child,
) => _middleChapter(child).extend({ step: z.literal(step) })

export const SchoolTestTreeSchema = z.array(
  z.union([
    _schoolTestStepChapter('PRACTICE_TEST', CustomWorkbookTreePracticeTestRoundSchema),
    _middleChapter(
      z.union([
        CustomWorkbookTreeLittleChapterSchema.extend({ step: z.literal('THEORY') }),
        _schoolTestStepChapter('PAIR_SIMILAR', CustomWorkbookTreeConceptSchema),
        _schoolTestStepChapter('SCHOOL_WORKBOOK', CustomWorkbookTreeConceptSchema),
      ]),
    ),
  ]),
)

export type MiddleChapterTree = z.infer<typeof MiddleChapterTreeSchema>
export type LittleChapterTree = z.infer<typeof LittleChapterTreeSchema>
export type StepTree = z.infer<typeof StepTreeSchema>
export type SchoolTestTree = z.infer<typeof SchoolTestTreeSchema>

/**
 * 만드는 방식에 맞는 문제 트리. 단원 묶음은 묶는 단위(G)에 따라 중단원 트리나 소단원 트리다.
 */
export type CustomWorkbookChapters<
  T extends CustomWorkbookType = CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
> = T extends ChapterCustomWorkbookType
  ? G extends 'MIDDLE_CHAPTER'
    ? MiddleChapterTree
    : LittleChapterTree
  : T extends StepCustomWorkbookType
    ? StepTree
    : SchoolTestTree

/** 트리의 모든 노드 */
export type CustomWorkbookTreeNode =
  | CustomWorkbookTreeMiddleChapter<unknown>
  | CustomWorkbookTreeLittleChapter
  | CustomWorkbookTreeConcept
  | CustomWorkbookTreePracticeTestRound
  | CustomWorkbookTreeProblem

/***************** 추천 문제 수 *****************/

// 학교별 기출은 단계마다의 수를 함께 낸다. totalProblemCount 는 셋(기출 유형·교과서 문제·실전 모의고사)의 합이고
// maxPracticeTestProblemCount 는 회차 조건을 빼고 뽑을 수 있는 실전 모의고사 문제 수다.
export const SchoolTestRecommendedProblemCountSchema = z.object({
  totalProblemCount: z.number(),
  pairSimilarProblemCount: z.number(),
  schoolWorkbookProblemCount: z.number(),
  practiceTestProblemCount: z.number(),
  maxPracticeTestProblemCount: z.number(),
})
export const RecommendedProblemCountSchema = z.object({ totalProblemCount: z.number() })
export type RecommendedProblemCount<T extends CustomWorkbookType = CustomWorkbookType> =
  T extends 'SCHOOL_TEST_PROBLEM_STEP'
    ? z.infer<typeof SchoolTestRecommendedProblemCountSchema>
    : z.infer<typeof RecommendedProblemCountSchema>

/***************** 내지 디자인 *****************/

export const InnerDesignTemplateSchema = z.object({
  id: z.literal([1, 2, 3, 4]),
  thumbnailImageUrl: z.string(),
  exampleImages: z.array(z.object({ url: z.string(), orderingNumber: z.number() })),
})
export type InnerDesignTemplate = z.infer<typeof InnerDesignTemplateSchema>

/***************** 내 교재 상세 *****************/

/**
 * 수정·복제할 내 교재. 목록의 내 교재와 필드가 겹치지만 서버 클래스가 다르다(CustomWorkbookWithFilterResponse).
 * 만든 방식·문제 조건·PDF 옵션·내지 디자인이 더 오고, 목록에만 있는 필드(publisher·academyId·makingUnit 등)는 없다.
 */
export const CustomWorkbookDetailSchema = CustomWorkbookSchema.pick({
  id: true,
  type: true,
  revision: true,
  schoolType: true,
  grade: true,
  semester: true,
  headtitle: true,
  title: true,
  subtitle: true,
  fulltitle: true,
  autoScored: true,
  createDatetime: true,
  thumbnailImageUrl: true,
  designTemplateId: true,
  partitionType: true,
  coverData: true,
  coverType: true,
  pdfStatus: true,
  answerPage: true,
  minPage: true,
  maxPage: true,
  coverPages: true,
  orderDatetime: true,
  featureType: true,
  productId: true,
  productDiscountRate: true,
  problemColorPrice: true,
  problemBwPrice: true,
  problemAnswerColorPrice: true,
  problemAnswerBwPrice: true,
  problemColorDiscountedPrice: true,
  problemBwDiscountedPrice: true,
  problemAnswerColorDiscountedPrice: true,
  problemAnswerBwDiscountedPrice: true,
}).extend({
  customWorkbookType: CustomWorkbookTypeSchema,
  innerDesignTemplateId: InnerDesignTemplateSchema.shape.id.nullable(),
  // 소단원마다 개념 쪽을 넣는가(단원 묶음)
  showLittleChapterTheory: z.boolean(),
  // 학교별 기출 PDF 에 개념명·정답률·난이도·태그를 넣는가
  includeConceptNameInPdf: z.boolean(),
  includeAnswerRateInPdf: z.boolean(),
  includeLevelInPdf: z.boolean(),
  includeTagTopInPdf: z.boolean(),
  filter: CustomWorkbookFilterSchema,
  // 원본 교재 문제로 만든 교재의 바탕 시중교재
  originWorkbook: z
    .object({
      id: z.number(),
      type: WorkbookTypeSchema,
      fulltitle: z.string(),
      securingType: WorkbookSecuringTypeSchema.optional(),
    })
    .nullable(),
})
export type CustomWorkbookDetail<T extends CustomWorkbookType = CustomWorkbookType> = Omit<
  z.infer<typeof CustomWorkbookDetailSchema>,
  'customWorkbookType' | 'filter'
> & { customWorkbookType: T; filter: CustomWorkbookFilter<T> }

/** 학교별 기출 교재 PDF 옵션 */
export type SchoolTestPdfOption = Pick<
  CustomWorkbookDetail,
  'includeConceptNameInPdf' | 'includeAnswerRateInPdf' | 'includeLevelInPdf' | 'includeTagTopInPdf'
>
